import type { ParamRpcHandler, RpcContext } from '@scayle/storefront-nuxt'
import { getProductPath } from '@scayle/storefront-nuxt'
import StoryblokClient, { type ISbResult } from 'storyblok-js-client'

const get = async (
  url: string,
  options: Record<string, any>,
  context: RpcContext,
) => {
  const isCmsPreview = options.version === 'draft'
  const accessToken = isCmsPreview
    ? process.env.NUXT_STORYBLOK_PREVIEW_ACCESS_TOKEN
    : process.env.NUXT_STORYBLOK_ACCESS_TOKEN

  const storyblok = new StoryblokClient(
    {
      accessToken,
    },
    'https://api.storyblok.com/v2',
  )

  const method = async ({
    url,
    options,
  }: {
    url: string
    options: Record<string, any>
  }) => {
    try {
      // prevent storyblok in-memory caching
      options.cv = +new Date()

      const story = await storyblok.get(url, options)
      const resolvedData = await resolveIdsToSlugs(story.data, context)

      // if pagination data is expected
      if (options.page) {
        return {
          ...resolvedData,
          total: story.total,
          perPage: story.perPage,
        }
      }

      return resolvedData
    } catch (e: any) {
      if (e.response?.status === 404 || e.status === 404) {
        return 404
      } // save '404' to redis cache
      throw e
    }
  }

  const result = isCmsPreview
    ? await method({ url, options })
    : await context.cached(method)({ url, options })
  // if the cache item is a 404, return null
  if (result === 404) {
    return null
  }
  return result
}

interface GetCmsContentOptions {
  starts_with?: string
  content_type?: string
  per_page?: number
  resolve_relations?: string
  filter_query?: Record<string, Record<string, any>>
  from_release?: string

  language?: string
  page?: number
  preview?: boolean
}

/** Returned when a story exists but is excluded for the current locale (treat as unpublished → 404). */
export function isCmsExcluded(result: unknown): result is { excluded: true } {
  return (
    typeof result === 'object' &&
    result !== null &&
    'excluded' in result &&
    (result as { excluded: boolean }).excluded
  )
}

export const getCmsContent: ParamRpcHandler<
  {
    slug: string
    options?: GetCmsContentOptions
  },
  ISbResult['data']['story'] | { excluded: true } | null
> = async (params, context: RpcContext) => {
  const fetch = async () => {
    const { slug, options } = params
    try {
      const url = await resolveToCmsUrl(slug, context)
      const { preview, ...otherOptions } = options || {}
      const path = `cdn/stories/${url}`
      const newOptions = {
        language: context.locale,
        version: preview ? 'draft' : 'published',
        resolve_links: 'url',
        ...otherOptions,
      }

      const fetchedData = await get(path, newOptions, context)

      // if pagination data is expected
      if (newOptions.page) {
        const { stories, total, perPage } = fetchedData || {}

        return stories ? { stories, total, perPage } : null
      }

      const { story, stories } = fetchedData || {}
      // Handle single story excluded countries
      if (story) {
        if (story.content?.excludedCountries?.includes(context.locale)) {
          return { excluded: true }
        }
        return story
      }

      // Handle multiple stories excluded countries
      if (stories) {
        const hasExcluded = stories.some((s: any) =>
          s.content?.excludedCountries?.includes(context.locale),
        )

        if (hasExcluded) {
          return { excluded: true } // throw 404 if any story is excluded
        }
        return stories
      }

      return null
    } catch (e: any) {
      if (e?.status === 404) {
        return null
      }
      context.log.error('Error fetching from Storyblok', e)
      throw e
    }
  }

  try {
    const { slug } = params
    // First attempt
    const result = await fetch()
    if (result !== null && result !== undefined) {
      return result
    }

    // Second attempt
    // Retry once if the first attempt failed (result is undefined)
    context.log.debug(`Retrying fetch for slug: ${slug}`)
    return await fetch()
  } catch (e: any) {
    // Log error and slug if both attempts fail
    context.log.error(
      `Error fetching CMS content for slug: ${params?.slug}, params: ${JSON.stringify(params)}`,
      e,
    )
    return null
  }
}

export const getDatasource: ParamRpcHandler<
  {
    datasource?: string
    preview?: boolean
  },
  [
    {
      id: number
      name: string
      value: string
    },
  ]
> = async (params, context: RpcContext) => {
  const datasource = params?.datasource
  const preview = Boolean(params?.preview)

  if (!datasource) {
    context.log.warn(
      `getDatasource called without datasource. params: ${JSON.stringify(params)}`,
    )
    return null
  }

  try {
    const fetchedData = await get(
      'cdn/datasource_entries',
      { datasource, per_page: 1000, version: preview ? 'draft' : 'published' }, // instead of using the per_page limit, it is possible to use StoryblokClient.getAll – requires refactoring
      context,
    )

    return fetchedData?.datasource_entries || null
  } catch (e) {
    context.log.error('Error fetching from Storyblok', e)
    return null
  }
}

const resolveUrl = async (url: string, context: RpcContext) => {
  const match = url.match(/\/?(categories|trends|lookbooks|products)\/(\d+)/)
  if (match) {
    const [, type, _id] = match
    if (!type || !_id) {
      return url
    }
    const id = parseInt(_id, 10)

    try {
      switch (type) {
        case 'categories':
        case 'lookbooks':
        case 'trends':
          return (
            await context.cached(context.sapiClient.categories.getById)(id)
          ).path
        case 'products':
          return getProductPath(
            await context.cached(context.sapiClient.products.getById)(id),
          )
      }
    } catch (e) {
      context.log.error('Error resolving url', e)
      return url
    }
  }

  return url
}

const resolveToCmsUrl = async (slug: string, context: RpcContext) => {
  const match = slug.match(
    /\/(women)\/(categories|trends|lookbooks|products)\/(\D+)/,
  )
  if (match) {
    const resolvedUrls = await context.cached(
      context.sapiClient.categories.getByPath,
    )([slug])

    return `${match[2]}/${resolvedUrls.id}`
  }

  return slug
}

export const resolveIdsToSlugs = async (
  content: Record<string, any>,
  context: RpcContext,
) => {
  for (const key in content) {
    if (!Object.prototype.hasOwnProperty.call(content, key)) {
      continue
    }
    const value = content[key]
    if (value) {
      // set resolved url (storyblok translatable slug) to the cached_url
      if (
        typeof value === 'object' &&
        value.cached_url !== undefined &&
        value.linktype === 'story' &&
        value.story !== undefined &&
        // edge case here - there can be a link from storyblok to another story
        // that is referencing a PLP. In this case we don't want to resolve
        // the url to the stories slug (which can be 'categories/70'), but rather
        // to the PLP (which could be 'damenmode/hosen')
        !value.cached_url.match(
          /\/?(categories|trends|lookbooks|products)\/(\d+)/,
        )
      ) {
        const fullSlug = value.story.full_slug
        const locale = context.locale.replace('_', '-').toLowerCase()
        const regex = new RegExp(`^/?${locale}`)

        const fullSlugWithoutLocalePrefix = fullSlug.replace(regex, '')
        content[key].cached_url = fullSlugWithoutLocalePrefix
        // modify urls to resolve our special case links
      } else if (typeof value === 'object' && value.cached_url !== undefined) {
        content[key].cached_url = await resolveUrl(value.cached_url, context)
      } else if (key === 'cta_url' && typeof value === 'string') {
        content[key] = await resolveUrl(value, context)
      } else if (key === 'cached_url' && typeof value === 'string') {
        content[key] = await resolveUrl(value, context)
      } else if (
        typeof value === 'object' &&
        value.href !== undefined &&
        value.story !== undefined
      ) {
        value.href = await resolveUrl(value.story.full_slug, context)
      } else if (Array.isArray(value)) {
        content[key] = await Promise.all(
          value.map((v: any) => resolveIdsToSlugs(v, context)),
        )
      } else if (typeof value === 'object') {
        content[key] = await resolveIdsToSlugs(value, context)
      } else if (key === 'component' && value === 'Image') {
        // Image is a reserved component name in Vue, so we use the CmsImage component name
        content[key] = 'CmsImage'
      } else if (key === 'component' && value === 'Text') {
        // Text is a reserved component name in Vue, so we use the CmsText component name
        content[key] = 'CmsText'
      }
    }
  }
  return content
}
